import { useState, useCallback, useRef, useEffect } from "react"
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import type { DragEndEvent, DragOverEvent } from "@dnd-kit/core"
import { useDragIndex } from "../contexts/drag-context"
import type { ITableColumnsType } from "../types"
import { getColumnKey } from "../utils/column-key"
import { useTableContext } from "../contexts/table-context"

const getColumnsOrder = (columns: ITableColumnsType<any>) => columns.map((col, index) => getColumnKey(col, index))

export const useDragAndDrop = <T = any>(initialColumns: ITableColumnsType<T>) => {
    const { setDragIndex } = useDragIndex()
    const { preferences } = useTableContext()

    const columnsLengthRef = useRef(initialColumns.length)

    const [columnOrder, setColumnOrder] = useState<string[]>(() => getColumnsOrder(initialColumns))

    useEffect(() => {
        if (initialColumns.length === columnsLengthRef.current) {
            return
        }
        columnsLengthRef.current = initialColumns.length

        setColumnOrder(getColumnsOrder(initialColumns))
    }, [initialColumns])

    // Update column order when preferences are loaded or reset
    useEffect(() => {
        const savedOrder = preferences?.state.columnOrder
        if (savedOrder && savedOrder.length > 0) {
            // Validate that all saved columns still exist and add any new ones
            const currentColumns = getColumnsOrder(initialColumns)
            const validSavedOrder = savedOrder.filter((key) => currentColumns.includes(key))
            const missingColumns = currentColumns.filter((key) => !validSavedOrder.includes(key))
            const newOrder = [...validSavedOrder, ...missingColumns]
            setColumnOrder(newOrder)
        } else if (preferences && savedOrder && savedOrder.length === 0) {
            // Preferences were reset, restore default order
            setColumnOrder(getColumnsOrder(initialColumns))
        }
    }, [preferences?.state.columnOrder, initialColumns, preferences])

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1
            }
        })
    )

    const onDragEnd = useCallback(
        ({ active, over }: DragEndEvent) => {
            if (active.id !== over?.id) {
                setColumnOrder((prevOrder) => {
                    const activeIndex = prevOrder.findIndex((key) => key === active?.id)
                    const overIndex = prevOrder.findIndex((key) => key === over?.id)

                    if (activeIndex === -1 || overIndex === -1) {
                        return prevOrder
                    }

                    const adjustedOverIndex = activeIndex < overIndex ? overIndex - 1 : overIndex
                    const newOrder = arrayMove(prevOrder, activeIndex, adjustedOverIndex)

                    // Update preferences directly
                    if (preferences) {
                        preferences.setColumnOrder(newOrder)
                    }

                    return newOrder
                })
            }
            setDragIndex({ active: -1, over: -1 })
        },
        [setDragIndex, preferences]
    )

    const onDragOver = useCallback(
        (columns: ITableColumnsType<T>) =>
            ({ active, over }: DragOverEvent) => {
                const activeIndex = columns.findIndex((i) => i.key === active.id)
                const overIndex = columns.findIndex((i) => i.key === over?.id)
                setDragIndex({
                    active: active.id,
                    over: over?.id,
                    direction: overIndex > activeIndex ? "right" : "left"
                })
            },
        [setDragIndex]
    )

    return {
        columnOrder,
        sensors,
        onDragEnd,
        onDragOver
    }
}
